export const MAX_ATTENDEES_LIMIT = 10000;
export const MAX_PRICE_LIMIT = 999;
export const MIN_PRICE_LIMIT = 0.5;

/**
 * Unified interface for both create and edit live event forms
 * This ensures consistency across both modals
 */
export interface LiveEventFormData {
  title: string;
  description: string;
  feeEuros: string;
  scheduledDate: string;
  scheduledTime: string;
  maxAttendees: string;
  whatsappUrl: string;
  ctaText: string;
}

// Type for translation function - flexible to work with any translation system
type TranslateFunction = (key: string, params?: Record<string, unknown>) => string;

/**
 * Creates validation functions that use the provided translate function
 * This allows the validators to work with any translation system
 */
export function createLiveEventValidators(translate: TranslateFunction) {
  /**
   * Validates event title - required and not empty
   */
  const validateTitle = (value: string): string | undefined => {
    if (!value || !value.trim()) {
      return translate("createLiveEventsModal.titleRequired");
    }
    return undefined;
  };

  /**
   * Validates event description - required and not empty
   */
  const validateDescription = (value: string): string | undefined => {
    if (!value || !value.trim()) {
      return translate("createLiveEventsModal.descriptionRequired");
    }
    return undefined;
  };

  /**
   * Validates event fee for premium events
   * @param value - Fee value as string
   * @param contentTier - "free" | "premium"
   */
  const validateFee = (value: string, contentTier: "free" | "premium"): string | undefined => {
    if (contentTier !== "premium") {
      return undefined;
    }
    if (!value || !value.trim()) {
      return translate("createLiveEventsModal.priceRequired");
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < MIN_PRICE_LIMIT) {
      return translate("createLiveEventsModal.pricePositive");
    }
    if (numValue > MAX_PRICE_LIMIT) {
      return translate("createLiveEventsModal.priceLimit");
    }
    return undefined;
  };

  /**
   * Validates event date - required and not empty
   */
  const validateDate = (value: string): string | undefined => {
    if (!value) {
      return translate("createLiveEventsModal.dateRequired");
    }
    return undefined;
  };

  /**
   * Validates event time - required and not empty
   */
  const validateTime = (value: string): string | undefined => {
    if (!value) {
      return translate("createLiveEventsModal.timeRequired");
    }
    return undefined;
  };

  /**
   * Validates scheduled date and time combination
   * @param date - Date string (YYYY-MM-DD format)
   * @param time - Time string (HH:MM format)
   * @param allowPast - Whether to allow past dates (for edit mode when event is live/ended)
   */
  const validateScheduledDateTime = (date: string, time: string, allowPast = false): string | undefined => {
    if (!date || !time) {
      return undefined; // Individual field validation will handle empty values
    }

    const scheduledDateTime = new Date(`${date}T${time}`);
    if (isNaN(scheduledDateTime.getTime())) {
      return translate("createLiveEventsModal.invalidDateTime");
    }

    if (!allowPast && scheduledDateTime <= new Date()) {
      return translate("createLiveEventsModal.dateMustBeFuture");
    }

    return undefined;
  };

  /**
   * Validates max attendees - optional but must be positive if provided
   */
  const validateMaxAttendees = (value: string): string | undefined => {
    if (!value || !value.trim()) {
      return undefined; // Optional field
    }
    const numValue = Number(value);
    if (isNaN(numValue) || numValue < 1) {
      return translate("createLiveEventsModal.maxAttendeesMustBePositive");
    }
    if (numValue > MAX_ATTENDEES_LIMIT) {
      return translate("createLiveEventsModal.maxAttendeesLimit");
    }
    return undefined;
  };

  /**
   * Creates a fee validator bound to a specific content tier
   * Useful for form controllers where contentTier may change
   */
  const createFeeValidator = (getContentTier: () => "free" | "premium") => {
    return (value: string): string | undefined => {
      return validateFee(value, getContentTier());
    };
  };

  /**
   * Creates a date/time validator that considers event status
   * Useful for edit mode where validation rules change based on event state
   */
  const createDateTimeValidator = (
    getDateTimeContext: () => {
      date: string;
      time: string;
      eventStatus: string;
      allowPast?: boolean;
    },
  ) => {
    return (): string | undefined => {
      const context = getDateTimeContext();
      const allowPast = context.allowPast || context.eventStatus === "live" || context.eventStatus === "ended";
      return validateScheduledDateTime(context.date, context.time, allowPast);
    };
  };

  return {
    validateTitle,
    validateDescription,
    validateFee,
    validateDate,
    validateTime,
    validateScheduledDateTime,
    validateMaxAttendees,
    createFeeValidator,
    createDateTimeValidator,
  };
}
